import type {
  Aura,
  EquippedItem,
  ParsedPlayerInfo,
  TalentBuild,
} from '@shared/types';
import {
  TALENT_TREES,
  TALENT_GRID_ROWS,
  TALENT_GRID_COLS,
  TREE_BACKGROUND_URLS,
  canAddPoint,
  canRemovePoint,
  encodeWowheadCalc,
  getPrereq,
  summarizeBuild,
  type TalentNode,
} from '@shared/talents';
import { t, tAuraName, tSlot, tTalent, wowheadDataAttr, wowheadUrl } from '@shared/i18n';
import { computeCharSheetStats, computeBuffedStats, type GearStatBonuses } from '@shared/charSheet';
import { lookupEnchant } from '@shared/enchants';
import { iconNameToUrl, lookup } from './wowhead';
import { attachTooltip } from './tooltip';

// Quality color (matches in-game tooltip border colors).
const QUALITY_COLORS: Record<number, string> = {
  1: '#ffffff', // common
  2: '#1eff00', // uncommon
  3: '#0070dd', // rare
  4: '#a335ee', // epic
  5: '#ff8000', // legendary
};

// Proxied via /api/img to bypass ZAM Network's geo-block (see wowhead.ts).
const ICON_BASE = '/api/img/images/wow/icons/medium';

function escapeHtml(s: string): string {
  return s.replace(/[&<>"']/g, (c) => {
    switch (c) {
      case '&': return '&amp;';
      case '<': return '&lt;';
      case '>': return '&gt;';
      case '"': return '&quot;';
      case "'": return '&#39;';
      default: return c;
    }
  });
}

function iconFilenameToUrl(icon: string): string {
  const base = icon.replace(/\.(jpg|png|gif)$/i, '');
  return `${ICON_BASE}/${base}.jpg`;
}

// ---- Gear ----

export function renderGear(items: EquippedItem[], container: HTMLElement): void {
  container.replaceChildren();
  if (items.length === 0) {
    container.textContent = t('gear.empty');
    return;
  }

  const table = document.createElement('table');
  table.className = 'gear-table';
  table.innerHTML = `
    <thead>
      <tr>
        <th>${t('gear.col.slot')}</th>
        <th>${t('gear.col.item')}</th>
        <th>${t('gear.col.ilvl')}</th>
        <th>${t('gear.col.enchants')}</th>
      </tr>
    </thead>
    <tbody></tbody>
  `;
  const tbody = table.querySelector('tbody')!;

  for (const item of items) {
    if (item.itemId === 0) {
      const tr = document.createElement('tr');
      tr.className = 'gear-row empty';
      tr.innerHTML = `
        <td class="slot">${escapeHtml(tSlot(item.slot))}</td>
        <td class="item muted">${t('gear.empty')}</td>
        <td class="ilvl"></td>
        <td class="enchants"></td>
      `;
      tbody.appendChild(tr);
      continue;
    }

    const color = QUALITY_COLORS[item.quality] ?? '#fff';
    const iconUrl = iconFilenameToUrl(item.icon);
    const wowheadHref = wowheadUrl('item', item.itemId);
    const wowheadAttr = wowheadDataAttr('item', item.itemId);

    // Render enchant placeholders. Each gets a data-spell-id we use to upgrade
    // the label to its localised name (and attach a hover tooltip) once the
    // wowhead lookup lands.
    const buildEnchant = (effectId: number, klass: string, fallbackKey: string): string => {
      const e = lookupEnchant(effectId);
      if (e) {
        // Temp weapon enchants prefer the consumable ITEM (sharpening
        // stone / oil) name over the buff spell name. Both forms still
        // link to wowhead's spell page so the tooltip works.
        const linkType: 'item' | 'spell' = e.itemId ? 'item' : 'spell';
        const linkId = e.itemId ?? e.spellId;
        return `<a class="enchant ${klass}" data-link-type="${linkType}" data-link-id="${linkId}" data-spell-id="${e.spellId}" data-effect-id="${effectId}" target="_blank" rel="noopener" href="${wowheadUrl(linkType, linkId)}">${escapeHtml(e.name)}</a>`;
      }
      return `<span class="enchant ${klass}" data-effect-id="${effectId}">${t(fallbackKey, { id: effectId })}</span>`;
    };

    const enchantBits: string[] = [];
    if (item.permanentEnchant) enchantBits.push(buildEnchant(item.permanentEnchant, '', 'gear.enchant.permanent'));
    if (item.temporaryEnchant) enchantBits.push(buildEnchant(item.temporaryEnchant, 'temp', 'gear.enchant.temporary'));
    const setBadge = item.setID ? `<span class="set-badge" title="set id">${t('gear.set', { id: item.setID })}</span>` : '';

    const tr = document.createElement('tr');
    tr.className = 'gear-row';
    tr.innerHTML = `
      <td class="slot">${escapeHtml(tSlot(item.slot))}</td>
      <td class="item">
        <a href="${wowheadHref}" target="_blank" rel="noopener" data-wowhead="${wowheadAttr}" style="color:${color}">
          <img class="icon" src="${iconUrl}" alt="" width="24" height="24" />
          <span class="item-name">${t('gear.itemPrefix')} ${item.itemId}</span>
        </a>
        ${setBadge}
      </td>
      <td class="ilvl">${item.itemLevel}</td>
      <td class="enchants">${enchantBits.join(' ') || '<span class="muted">—</span>'}</td>
    `;
    tbody.appendChild(tr);

    // Async upgrade with the localised wowhead name + icon.
    void lookup('item', item.itemId).then((r) => {
      if (r.name) {
        const nameEl = tr.querySelector<HTMLSpanElement>('.item-name');
        if (nameEl) nameEl.textContent = r.name;
      }
      if (r.icon) {
        const img = tr.querySelector<HTMLImageElement>('img.icon');
        if (img) img.src = iconNameToUrl(r.icon);
      }
    });

    // Item link gets a hover tooltip.
    const itemLink = tr.querySelector<HTMLAnchorElement>('td.item a');
    if (itemLink) attachTooltip(itemLink, 'item', item.itemId, item.quality);

    // Each enchant link/span gets its localised name + tooltip. Temp weapon
    // enchants (sharpening stones / oils) look up via ITEM so the user sees
    // 元素磨刀石 (the item name) instead of 致命武器 (the spell-buff name).
    // Permanent enchants and any without itemId fall back to spell lookup.
    tr.querySelectorAll<HTMLElement>('.enchant').forEach((node) => {
      const linkType = (node.dataset.linkType ?? 'spell') as 'spell' | 'item';
      const linkId = Number.parseInt(node.dataset.linkId ?? node.dataset.spellId ?? '', 10);
      if (!Number.isFinite(linkId) || linkId <= 0) return;
      attachTooltip(node, linkType, linkId);
      void lookup(linkType, linkId).then((r) => {
        if (r.name) node.textContent = r.name;
      });
    });
  }

  container.appendChild(table);
}

// ---- Auras ----

function groupAuras(auras: Aura[]): { starting: Aura[]; persistent: Aura[]; transient: Aura[]; assumed: Aura[] } {
  const starting: Aura[] = [];
  const persistent: Aura[] = [];
  const transient: Aura[] = [];
  const assumed: Aura[] = [];
  for (const a of auras) {
    if (a.origin === 'assumed') assumed.push(a);
    else if (a.origin === 'persistent') persistent.push(a);
    else if (a.origin === 'transient') transient.push(a);
    else starting.push(a);
  }
  return { starting, persistent, transient, assumed };
}

function renderAuraGroup(label: string, group: Aura[], showUptime: boolean): HTMLElement {
  const wrapper = document.createElement('div');
  wrapper.className = 'aura-group';

  const heading = document.createElement('h4');
  heading.className = 'aura-group-heading muted small';
  heading.textContent = `${label} · ${group.length}`;
  wrapper.appendChild(heading);

  const ul = document.createElement('ul');
  ul.className = 'aura-list';

  for (const a of group) {
    const iconUrl = a.icon ? iconFilenameToUrl(a.icon) : '';
    const wowheadHref = wowheadUrl('spell', a.abilityId);
    const wowheadAttr = wowheadDataAttr('spell', a.abilityId);
    const displayName = tAuraName(a.abilityId, a.name);
    const iconImg = iconUrl
      ? `<img class="icon" src="${iconUrl}" alt="" width="20" height="20" />`
      : '<img class="icon" src="" alt="" width="20" height="20" style="visibility:hidden" />';
    const uptimeStr = showUptime && a.uptimeMs
      ? ` <span class="muted small">${(a.uptimeMs / 1000).toFixed(0)}s</span>`
      : '';
    const li = document.createElement('li');
    li.className = 'aura';
    li.innerHTML = `
      <a href="${wowheadHref}" target="_blank" rel="noopener" data-wowhead="${wowheadAttr}">
        ${iconImg}
        <span class="aura-name">${escapeHtml(displayName)}</span>
        <span class="aura-id muted small">#${a.abilityId}</span>${uptimeStr}
      </a>
    `;
    ul.appendChild(li);

    // Hide on broken-image so we don't show the "image missing" glyph for
    // spellIDs whose wowhead page 301s to notFound (some classic-only buffs).
    const initialImg = li.querySelector<HTMLImageElement>('img.icon');
    if (initialImg) {
      initialImg.addEventListener('error', () => { initialImg.style.visibility = 'hidden'; }, { once: true });
    }

    // Async upgrade with localised wowhead name + icon.
    void lookup('spell', a.abilityId).then((r) => {
      if (r.name) {
        const nameEl = li.querySelector<HTMLSpanElement>('.aura-name');
        if (nameEl) nameEl.textContent = r.name;
      }
      if (r.icon) {
        const img = li.querySelector<HTMLImageElement>('img.icon');
        if (img) {
          img.src = iconNameToUrl(r.icon);
          img.style.visibility = '';
        }
      }
    });

    // Hover tooltip on the aura row.
    const auraLink = li.querySelector<HTMLAnchorElement>('a');
    if (auraLink) attachTooltip(auraLink, 'spell', a.abilityId);
  }

  wrapper.appendChild(ul);
  return wrapper;
}

export function renderAuras(auras: Aura[], container: HTMLElement): void {
  container.replaceChildren();
  if (auras.length === 0) {
    container.textContent = t('gear.empty');
    return;
  }

  const { starting, persistent, transient, assumed } = groupAuras(auras);

  if (assumed.length > 0) {
    container.appendChild(renderAuraGroup(t('aura.group.assumed'), assumed, false));
  }
  if (starting.length > 0) {
    container.appendChild(renderAuraGroup(t('aura.group.starting'), starting, false));
  }
  if (persistent.length > 0) {
    container.appendChild(renderAuraGroup(t('aura.group.persistent'), persistent, true));
  }
  if (transient.length > 0) {
    const details = document.createElement('details');
    details.className = 'aura-details';
    const summary = document.createElement('summary');
    summary.textContent = t('aura.group.transient', { n: transient.length });
    details.appendChild(summary);
    details.appendChild(renderAuraGroup(t('aura.group.transient', { n: transient.length }), transient, true));
    container.appendChild(details);
  }
}

// ---- Faction picker + per-buff toggles for assumed raid buffs ----

import {
  RAID_BUFFS,
  BUFF_CATALOG,
  buffsForFaction,
  buffsByCategory,
  detectFaction,
  type Faction,
  type BuffDef,
  type BuffCategory,
  type BuffEffects,
  type RaidBuffDef,
} from '@shared/raidBuffs';

const FACTION_KEY = 'wcl-warrior-analyzer.faction';
const TOGGLE_KEY = 'wcl-warrior-analyzer.assumedToggles';

function loadFaction(): Faction {
  try {
    const v = localStorage.getItem(FACTION_KEY);
    if (v === 'alliance' || v === 'horde' || v === 'auto') return v;
  } catch { /* ignore */ }
  return 'auto';
}
function saveFaction(f: Faction): void {
  try { localStorage.setItem(FACTION_KEY, f); } catch { /* ignore */ }
}

interface ToggleMap { [spellId: string]: boolean }
function loadToggles(): ToggleMap {
  try {
    const v = localStorage.getItem(TOGGLE_KEY);
    if (v) return JSON.parse(v) as ToggleMap;
  } catch { /* ignore */ }
  return {};
}
function saveToggles(m: ToggleMap): void {
  try { localStorage.setItem(TOGGLE_KEY, JSON.stringify(m)); } catch { /* ignore */ }
}

export interface AssumedState {
  faction: Faction;
  resolvedFaction: 'alliance' | 'horde' | 'unknown';
  toggles: ToggleMap;
}

export function loadAssumedState(): AssumedState {
  return { faction: loadFaction(), resolvedFaction: 'unknown', toggles: loadToggles() };
}

function resolveFaction(state: AssumedState, classRoster: ReadonlyArray<string>): 'alliance' | 'horde' | 'unknown' {
  if (state.faction === 'alliance') return 'alliance';
  if (state.faction === 'horde') return 'horde';
  return detectFaction(classRoster);
}

// Returns the spell IDs the user (effectively) toggled ON.
// For RAID category we apply faction filter; world / consumable / food / self
// ignore faction (anyone can have them on).
export function activeAssumedSpellIds(state: AssumedState, classRoster: ReadonlyArray<string>): number[] {
  const resolved = resolveFaction(state, classRoster);
  const ids: number[] = [];
  for (const def of BUFF_CATALOG) {
    // Raid buffs only count if their faction matches; non-raid (world /
    // consumable / food / self) are faction-agnostic.
    if (def.category === 'raid') {
      if (resolved === 'unknown') continue;
      if (def.faction !== 'both' && def.faction !== resolved) continue;
    }
    const v = state.toggles[String(def.spellId)];
    const on = v === undefined ? def.defaultOn : v;
    if (on) ids.push(def.spellId);
  }
  return ids;
}

// Returns the BuffEffects array for everything the user has ticked. Used by
// the buffed-stats view to compute final str/agi/AP/Crit/etc. Each element
// is the .effects object from BUFF_CATALOG (passed by reference; do not
// mutate). Order matters only for attrMult — multiplications are commutative
// so list order is fine.
export function activeAssumedEffects(state: AssumedState, classRoster: ReadonlyArray<string>): BuffEffects[] {
  const ids = new Set(activeAssumedSpellIds(state, classRoster));
  const out: BuffEffects[] = [];
  for (const def of BUFF_CATALOG) {
    if (ids.has(def.spellId)) out.push(def.effects);
  }
  return out;
}

export function renderAssumedPicker(
  container: HTMLElement,
  state: AssumedState,
  classRoster: ReadonlyArray<string>,
  onChange: () => void,
): void {
  container.replaceChildren();

  const wrap = document.createElement('div');
  wrap.className = 'assumed-picker';

  const heading = document.createElement('h4');
  heading.className = 'assumed-heading';
  heading.textContent = t('assumed.heading');
  wrap.appendChild(heading);

  const help = document.createElement('p');
  help.className = 'muted small';
  help.textContent = t('assumed.help');
  wrap.appendChild(help);

  // Faction picker
  const facBox = document.createElement('div');
  facBox.className = 'assumed-faction';
  facBox.innerHTML = `<span class="muted small">${escapeHtml(t('assumed.faction'))}:</span>`;
  for (const f of ['alliance', 'horde', 'auto'] as const) {
    const id = `faction-${f}`;
    const label = document.createElement('label');
    label.htmlFor = id;
    label.innerHTML =
      `<input type="radio" name="faction" id="${id}" value="${f}" ${state.faction === f ? 'checked' : ''} /> ` +
      escapeHtml(t(`assumed.faction.${f}`));
    facBox.appendChild(label);
  }
  wrap.appendChild(facBox);

  state.resolvedFaction = resolveFaction(state, classRoster);

  // Auto-detect badge
  if (state.faction === 'auto') {
    const detected = document.createElement('p');
    detected.className = 'muted small';
    detected.textContent = t(`assumed.detected.${state.resolvedFaction}`);
    wrap.appendChild(detected);
  }

  // Toggle list — grouped by buff category. Raid buffs are filtered by
  // resolved faction; world / consumable / food / self show all entries.
  const renderGroup = (label: string, defs: ReadonlyArray<BuffDef>): void => {
    if (defs.length === 0) return;
    const det = document.createElement('details');
    det.className = 'assumed-group';
    // Open by default for categories that have anything ticked, OR for the
    // raid category since that's where most users start. Other categories
    // start collapsed to keep the panel compact.
    const anyOn = defs.some((d) => {
      const v = state.toggles[String(d.spellId)];
      return v === undefined ? d.defaultOn : v;
    });
    det.open = label === t('assumed.group.raid') || anyOn;
    const summary = document.createElement('summary');
    summary.className = 'assumed-group-label';
    summary.textContent = label;
    det.appendChild(summary);
    const list = document.createElement('ul');
    list.className = 'assumed-toggle-list';
    for (const def of defs) {
      list.appendChild(renderAssumedToggleRow(def, state, onChange));
    }
    det.appendChild(list);
    wrap.appendChild(det);
  };

  const raidBuffs = state.resolvedFaction === 'unknown' ? [] : buffsForFaction(state.resolvedFaction);
  renderGroup(t('assumed.group.raid'),       raidBuffs);
  renderGroup(t('assumed.group.world'),      buffsByCategory('world'));
  renderGroup(t('assumed.group.consumable'), buffsByCategory('consumable'));
  renderGroup(t('assumed.group.food'),       buffsByCategory('food'));
  renderGroup(t('assumed.group.self'),       buffsByCategory('self'));

  container.appendChild(wrap);

  // Wire faction radios
  facBox.querySelectorAll<HTMLInputElement>('input[name="faction"]').forEach((radio) => {
    radio.addEventListener('change', () => {
      const v = radio.value as Faction;
      state.faction = v;
      saveFaction(v);
      onChange();
    });
  });
}

function renderAssumedToggleRow(def: BuffDef, state: AssumedState, onChange: () => void): HTMLLIElement {
  const li = document.createElement('li');
  li.className = 'assumed-toggle';
  const sid = def.spellId;
  const stored = state.toggles[String(sid)];
  const on = stored === undefined ? def.defaultOn : stored;
  const wowheadHref = wowheadUrl('spell', sid);
  const wowheadAttr = wowheadDataAttr('spell', sid);
  const iconUrl = iconNameToUrl(def.iconName);
  li.innerHTML = `
    <label>
      <input type="checkbox" data-sid="${sid}" ${on ? 'checked' : ''} />
      <a href="${wowheadHref}" target="_blank" rel="noopener" data-wowhead="${wowheadAttr}" data-sid="${sid}" class="assumed-name">
        <img class="assumed-icon" src="${iconUrl}" alt="" width="16" height="16" />
        <span class="assumed-label">${escapeHtml(def.enKey)}</span>
      </a>
    </label>
  `;
  // Async upgrade name + icon to localised wowhead value. Target the inner
  // <span class="assumed-label"> so we don't blow away the icon <img>.
  void lookup('spell', sid).then((r) => {
    if (r.name) {
      const lbl = li.querySelector<HTMLSpanElement>('span.assumed-label');
      if (lbl) lbl.textContent = r.name;
    }
    if (r.icon) {
      const img = li.querySelector<HTMLImageElement>('img.assumed-icon');
      if (img) img.src = iconNameToUrl(r.icon);
    }
  });
  // Hover tooltip on the spell link.
  const link = li.querySelector<HTMLAnchorElement>('a.assumed-name');
  if (link) attachTooltip(link, 'spell', sid);
  // Toggle handler
  const cb = li.querySelector<HTMLInputElement>('input[type="checkbox"]')!;
  cb.addEventListener('change', () => {
    state.toggles[String(sid)] = cb.checked;
    saveToggles(state.toggles);
    onChange();
  });
  return li;
}

// ---- Talents (game-style grid, left-click adds, right-click removes) ----

interface TalentRenderOptions {
  build: TalentBuild;
  onChange: (build: TalentBuild) => void;
}

export function renderTalents(
  parsed: ParsedPlayerInfo,
  container: HTMLElement,
  opts: TalentRenderOptions,
): void {
  container.replaceChildren();

  const summaryEl = document.createElement('p');
  summaryEl.className = 'talent-summary';
  container.appendChild(summaryEl);

  // Link out to Wowhead's official Chinese calculator with the current build
  // pre-applied. Wowhead refuses iframe embedding (X-Frame-Options: DENY) so
  // we can't host their UI inline; this link is the next-best path for users
  // who want the polished experience.
  const wowheadLinkWrap = document.createElement('p');
  wowheadLinkWrap.className = 'talent-wowhead-link';
  const wowheadLink = document.createElement('a');
  wowheadLink.target = '_blank';
  wowheadLink.rel = 'noopener';
  wowheadLink.textContent = t('panel.talents.openWowhead');
  wowheadLinkWrap.appendChild(wowheadLink);
  container.appendChild(wowheadLinkWrap);

  const trees = document.createElement('div');
  trees.className = 'talent-trees-grid';
  container.appendChild(trees);

  // One <div> per tree, each holding a 7x4 CSS grid of cells.
  const cellNodes = new Map<string, HTMLAnchorElement>();

  // Track arrow elements per child fieldName so we can flip their active state
  // when the parent transitions between empty / partial / full.
  const arrowNodes = new Map<string, HTMLDivElement>();

  for (const tree of TALENT_TREES) {
    const treeBox = document.createElement('div');
    treeBox.className = `talent-tree-box tree-${tree.name.toLowerCase()}`;
    const bgUrl = TREE_BACKGROUND_URLS[tree.name];
    treeBox.style.setProperty('--tree-bg', `url("${bgUrl}")`);

    const expected = parsed.treePoints[tree.name.toLowerCase() as 'arms' | 'fury' | 'protection'];
    const treeName = t(`talents.tree.${tree.name}`);

    const header = document.createElement('div');
    header.className = 'talent-tree-header';
    header.innerHTML = `
      <h4>${escapeHtml(treeName)}</h4>
      <span class="talent-tree-points" data-tree="${tree.name.toLowerCase()}">
        0 / <span class="muted">${expected}</span>
      </span>
      <button type="button" class="talent-tree-reset" title="${escapeHtml(t('panel.talents.resetTip'))}">${escapeHtml(t('panel.talents.reset'))}</button>
    `;
    treeBox.appendChild(header);

    // Click handler for the per-tree reset button: zero out every node in
    // this tree, then re-render.
    const resetBtn = header.querySelector<HTMLButtonElement>('.talent-tree-reset');
    if (resetBtn) {
      resetBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        for (const node of tree.nodes) delete opts.build[node.fieldName];
        opts.onChange(opts.build);
        refresh();
      });
    }

    const grid = document.createElement('div');
    grid.className = 'talent-grid';
    grid.style.gridTemplateRows = `repeat(${TALENT_GRID_ROWS}, 1fr)`;
    grid.style.gridTemplateColumns = `repeat(${TALENT_GRID_COLS}, 1fr)`;
    treeBox.appendChild(grid);

    // Arrows go in BEFORE cells in document order so they land below in z-stack.
    for (const node of tree.nodes) {
      const prereq = getPrereq(node.fieldName);
      if (!prereq) continue;
      // Vanilla warrior prereqs are all same-column "down" arrows; the
      // prereq sits above the child by 1 or 2 rows.
      if (prereq.colIdx !== node.colIdx) continue;

      const arrow = document.createElement('div');
      arrow.className = 'talent-arrow-down';
      arrow.style.gridColumn = String(node.colIdx + 1);
      // Span the rows from the prereq row through the child row so the arrow
      // visually connects them.
      arrow.style.gridRow = `${prereq.rowIdx + 1} / ${node.rowIdx + 2}`;
      arrow.dataset.rowSize = String(node.rowIdx - prereq.rowIdx);
      arrow.dataset.childField = node.fieldName;
      grid.appendChild(arrow);
      arrowNodes.set(node.fieldName, arrow);
    }

    for (const node of tree.nodes) {
      const cell = makeTalentCell(node, opts);
      grid.appendChild(cell);
      cellNodes.set(node.fieldName, cell);
    }

    trees.appendChild(treeBox);
  }

  function refresh(): void {
    const sum = summarizeBuild(opts.build);
    const total = sum.arms + sum.fury + sum.protection;
    const expected = parsed.treePoints;
    const expectedTotal = expected.arms + expected.fury + expected.protection;
    const treeWarn =
      sum.arms !== expected.arms || sum.fury !== expected.fury || sum.protection !== expected.protection;
    summaryEl.innerHTML = `
      <strong>${t('talents.allocated')}:</strong>
      <span class="${sum.arms === expected.arms ? '' : 'mismatch'}">${t('talents.armsLabel', { n: sum.arms })}</span> /
      <span class="${sum.fury === expected.fury ? '' : 'mismatch'}">${t('talents.furyLabel', { n: sum.fury })}</span> /
      <span class="${sum.protection === expected.protection ? '' : 'mismatch'}">${t('talents.protLabel', { n: sum.protection })}</span>
      &nbsp;&nbsp;<span class="muted">${t('talents.total', { cur: total, max: expectedTotal })}</span>
      ${treeWarn ? `<span class="warn">${t('talents.warn')}</span>` : ''}
    `;

    // Update tree counters in headers.
    container.querySelectorAll<HTMLSpanElement>('.talent-tree-points').forEach((el) => {
      const tree = el.dataset.tree as 'arms' | 'fury' | 'protection';
      const cur = sum[tree];
      const exp = expected[tree];
      el.innerHTML = `${cur} / <span class="muted">${exp}</span>`;
      el.classList.toggle('mismatch', cur !== exp);
    });

    // Update each cell's state.
    for (const tree of TALENT_TREES) {
      for (const node of tree.nodes) {
        const cell = cellNodes.get(node.fieldName);
        if (!cell) continue;
        updateCell(cell, node, opts.build);
      }
    }

    // Update each prereq arrow: active when the parent is at max points.
    for (const [childField, arrow] of arrowNodes) {
      const prereq = getPrereq(childField);
      if (!prereq) continue;
      const parentPts = opts.build[prereq.fieldName] ?? 0;
      if (parentPts >= prereq.maxPoints) arrow.dataset.active = 'true';
      else delete arrow.dataset.active;
    }

    // Refresh Wowhead calc link with the current build encoded.  Routed via
    // our /api/wh reverse proxy so users in geo-blocked regions still reach
    // the calculator.
    const encoded = encodeWowheadCalc(opts.build);
    wowheadLink.href = `/api/wh/classic/cn/talent-calc/warrior/${encoded}`;
  }

  function makeTalentCell(node: TalentNode, opts: TalentRenderOptions): HTMLAnchorElement {
    const spellId = node.spellIds[0] ?? 0;
    const cell = document.createElement('a');
    cell.className = 'talent-cell';
    cell.style.gridRow = String(node.rowIdx + 1);
    cell.style.gridColumn = String(node.colIdx + 1);
    cell.href = wowheadUrl('spell', spellId);
    cell.target = '_blank';
    cell.rel = 'noopener';
    cell.dataset.field = node.fieldName;
    cell.setAttribute('data-wowhead', wowheadDataAttr('spell', spellId));

    cell.innerHTML = `
      <span class="talent-cell-name"></span>
      <span class="talent-cell-points"></span>
    `;

    // Pull the real Chinese (or English) name + icon from Wowhead, then upgrade
    // the cell: hide the placeholder text, set the icon as the cell background.
    if (spellId) {
      void lookup('spell', spellId).then((r) => {
        const nameEl = cell.querySelector<HTMLSpanElement>('.talent-cell-name');
        if (r.name && nameEl) nameEl.textContent = r.name;
        if (r.icon) {
          cell.style.backgroundImage = `url("${iconNameToUrl(r.icon)}")`;
          cell.classList.add('has-icon');
        }
      });
      attachTooltip(cell, 'spell', spellId);
    }

    // Single canonical handler per direction. Previously we listened on
    // mousedown + pointerdown + contextmenu all at once, so a right click
    // fired several remove paths (mousedown.button=2 then contextmenu) and
    // dropped the talent by 2 per click. Now: mousedown handles left-click
    // only, contextmenu handles right-click only.
    cell.addEventListener('mousedown', (e) => {
      if (e.button !== 0) return;          // ignore middle/right here
      e.preventDefault();
      e.stopPropagation();
      if (e.ctrlKey || e.metaKey) tryRemove(node, opts);
      else tryAdd(node, opts);
      refresh();
    });
    cell.addEventListener('contextmenu', (e) => {
      e.preventDefault();
      e.stopPropagation();
      tryRemove(node, opts);
      refresh();
    });
    // Suppress default link-navigation / middle-click pasteboard / etc.
    cell.addEventListener('click',    (e) => { e.preventDefault(); e.stopPropagation(); });
    cell.addEventListener('auxclick', (e) => { e.preventDefault(); e.stopPropagation(); });

    return cell;
  }

  function tryAdd(node: TalentNode, opts: TalentRenderOptions): void {
    if (!canAddPoint(node, opts.build).ok) return;
    const cur = opts.build[node.fieldName] ?? 0;
    opts.build[node.fieldName] = cur + 1;
    opts.onChange(opts.build);
  }

  function tryRemove(node: TalentNode, opts: TalentRenderOptions): void {
    if (!canRemovePoint(node, opts.build).ok) return;
    const cur = opts.build[node.fieldName] ?? 0;
    if (cur - 1 <= 0) delete opts.build[node.fieldName];
    else opts.build[node.fieldName] = cur - 1;
    opts.onChange(opts.build);
  }

  function updateCell(cell: HTMLAnchorElement, node: TalentNode, build: TalentBuild): void {
    const cur = build[node.fieldName] ?? 0;
    const max = node.maxPoints;
    const canAdd = canAddPoint(node, build).ok;

    cell.classList.toggle('has-points', cur > 0);
    cell.classList.toggle('full', cur >= max);
    cell.classList.toggle('locked', cur === 0 && !canAdd);
    cell.classList.toggle('can-add', canAdd && cur < max);

    const nameEl = cell.querySelector<HTMLSpanElement>('.talent-cell-name')!;
    const ptsEl = cell.querySelector<HTMLSpanElement>('.talent-cell-points')!;
    nameEl.textContent = tTalent(node.fieldName);
    ptsEl.textContent = `${cur}/${max}`;

    cell.title = `${tTalent(node.fieldName)} — ${cur}/${max}`;
  }

  refresh();
}

// ---- Stats ----

// Stock-style delta annotation: ▲N in green when value > compare,
// ▼N in red when smaller, hidden when equal. Returned HTML is wrapped in
// a single span so the parens + triangle + number are all coloured but the
// surrounding "Strength 511" label stays default-coloured. Used only when
// rendering slot B in compare mode (compareTo = slot A's stats).
function deltaSpan(diff: number, inverted = false): string {
  if (diff === 0) return '';
  const arrow = diff > 0 ? '▲' : '▼';
  const isGood = inverted ? diff < 0 : diff > 0;
  const cls = isGood ? 'delta good' : 'delta bad';
  return ` <span class="${cls}">(${arrow}${Math.abs(diff)})</span>`;
}

export type StatsViewMode = 'base' | 'buffed';

export function renderStats(
  parsed: ParsedPlayerInfo,
  container: HTMLElement,
  opts?: {
    compareTo?: ParsedPlayerInfo;
    gearStats?: GearStatBonuses;          // base stats summed from item tooltips
    compareGearStats?: GearStatBonuses;
    viewMode?: StatsViewMode;             // 'base' = unbuffed (matches in-town char sheet); 'buffed' = base + selected buffs (self-computed)
    onToggleViewMode?: (mode: StatsViewMode) => void;
    // Active buff effects from the assumed-buff picker (raid + world +
    // consumable + food + self). Used to compute the buffed view stats
    // entirely from base — we do NOT consult parsed.stats for the buffed
    // view since vanilla CombatantInfo is unreliable for buff capture.
    buffEffects?: import('@shared/raidBuffs').BuffEffects[];
    compareBuffEffects?: import('@shared/raidBuffs').BuffEffects[];
  },
): void {
  const viewMode: StatsViewMode = opts?.viewMode ?? 'base';
  const cs = computeCharSheetStats(parsed, 60, opts?.gearStats);
  const cmpCs = opts?.compareTo
    ? computeCharSheetStats(opts.compareTo, 60, opts.compareGearStats)
    : null;

  // Compute buffed stats from base + active buff effects. We compute these
  // unconditionally (cheap) — the view picks which one to display. If the
  // user has no buffs ticked, buffed === base for every field.
  const ZG: GearStatBonuses = { str:0,agi:0,sta:0,int:0,spi:0,ap:0,crit:0,armor:0 };
  const buffed = computeBuffedStats(cs, parsed, opts?.gearStats ?? ZG, opts?.buffEffects ?? [], 60);
  const cmpBuffed = cmpCs == null
    ? null
    : computeBuffedStats(cmpCs, opts!.compareTo!, opts?.compareGearStats ?? ZG, opts?.compareBuffEffects ?? [], 60);

  // 'base' view: race+class + gear summed from tooltips (matches in-town
  // character sheet). 'buffed' view: base + every selected buff effect
  // applied (matches what the in-game character sheet shows after the
  // user pops their pre-pull buffs / consumables / food).
  const showBase = viewMode === 'base';
  const stats = showBase
    ? cs.baseStats
    : { strength: buffed.strength, agility: buffed.agility, stamina: buffed.stamina,
        intellect: buffed.intellect, spirit: buffed.spirit };
  const cmpStats = showBase
    ? cmpCs?.baseStats
    : (cmpBuffed
        ? { strength: cmpBuffed.strength, agility: cmpBuffed.agility, stamina: cmpBuffed.stamina,
            intellect: cmpBuffed.intellect, spirit: cmpBuffed.spirit }
        : undefined);

  const ap        = showBase ? cs.attackPower      : buffed.attackPower;
  const cmpAp     = cmpCs == null ? null : (showBase ? cmpCs.attackPower : cmpBuffed!.attackPower);
  const critPct   = showBase ? cs.meleeCritChance  : buffed.meleeCritChance;
  const cmpCritPct = cmpCs == null ? null : (showBase ? cmpCs.meleeCritChance : cmpBuffed!.meleeCritChance);

  const item = (label: string, value: number, baseline?: number) => {
    const delta = baseline != null ? deltaSpan(value - baseline) : '';
    return `<div class="stat-item"><span class="stat-label">${label}</span><span class="stat-value">${value}${delta}</span></div>`;
  };
  // Crit delta is in fraction units; deltaSpan expects integer/percent
  // diff. We display crit as a percentage with 2 decimals, so the diff
  // is rendered as a pp-precision percentage.
  const apDelta = cmpAp != null ? deltaSpan(ap - cmpAp) : '';
  const critDeltaRaw = cmpCritPct != null ? critPct - cmpCritPct : 0;
  let critDelta = '';
  if (cmpCritPct != null && Math.abs(critDeltaRaw) >= 0.0005) {
    const arrow = critDeltaRaw > 0 ? '▲' : '▼';
    const cls = critDeltaRaw > 0 ? 'delta good' : 'delta bad';
    critDelta = ` <span class="${cls}">(${arrow}${(Math.abs(critDeltaRaw) * 100).toFixed(2)}%)</span>`;
  }

  // Mode toggle: radio-style buttons let the user flip between "base" (the
  // in-town character sheet — gear + race+class only) and "buffed" (the
  // values WCL captured at fight start, with all raid buffs / consumables /
  // world buffs already applied). The same warriors look very different
  // numerically in each view, but both are correct snapshots — they
  // answer different questions ("is my gear good" vs "what was my
  // fight-time stat budget").
  // Crit breakdown — shown as a hover tooltip on the crit value AND as
  // an always-visible expandable details block below the row, so the user
  // can verify "where each percentage comes from" without DevTools.
  // Every component is computed from gear/enchants/talents — no WCL crit
  // data is consulted (vanilla CombatantInfo doesn't expose melee crit
  // anyway, but we want this self-derivation to be transparent).
  // Pick base-view OR buffed-view breakdown depending on which mode is
  // active. Buffed view's breakdown shows the buffed agility (so the
  // "from agility" % matches the buffed crit), and adds explicit "from
  // buffs" rows for the addAp / addCrit contributions that aren't in
  // the base formula. This way the user can audit:
  //   buffed AP  = base 160 + 2 × buffed_str + gear_AP + buff_AP
  //   buffed crit = 0.65 + buffed_agi/20 + cruelty + stance + gear_crit + buff_crit
  // and confirm each line matches their in-game character sheet.
  const cb = cs.critBreakdown;
  const ab = cs.apBreakdown;
  const bc = buffed.buffContrib;
  const dispStr = showBase ? cs.baseStats.strength : buffed.strength;
  const dispAgi = showBase ? cs.baseStats.agility  : buffed.agility;
  const dispApStr  = 2 * dispStr;
  const dispApGear = ab.fromGear;
  const dispApBuff = showBase ? 0 : bc.addAp;
  const dispCritAgi = (dispAgi / 20);
  const dispCritGear = cb.fromGear;
  const dispCritBuff = showBase ? 0 : bc.addCrit;
  const critTip = [
    `${t('crit.bd.classBase')}  : ${cb.classBase.toFixed(2)}%`,
    `${t('crit.bd.agility')}   : ${dispCritAgi.toFixed(2)}%  (${dispAgi} agi / 20)`,
    `${t('crit.bd.cruelty')}   : ${cb.fromCruelty.toFixed(2)}%  (${parsed.inferredBuild['cruelty'] ?? 0}/5)`,
    `${t('crit.bd.stance')}    : ${cb.fromStance.toFixed(2)}%  ${cs.inBerserkerStance ? t('crit.bd.stance.berserker') : t('crit.bd.stance.none')}`,
    `${t('crit.bd.gear')}      : ${dispCritGear.toFixed(2)}%  (${t('crit.bd.gear.note')})`,
    ...(showBase ? [] : [`${t('crit.bd.buffs')}     : ${dispCritBuff.toFixed(2)}%  (${t('crit.bd.buffs.note')})`]),
    `${'─'.repeat(30)}`,
    `${t('crit.bd.total')}     : ${(critPct * 100).toFixed(2)}%`,
  ].join('\n');
  const apTip = [
    `${t('ap.bd.classBase')}    : ${ab.classBase}  (${t('ap.bd.classBase.note')})`,
    `${t('ap.bd.strength')}     : ${dispApStr}  (2 × ${dispStr})`,
    `${t('ap.bd.gear')}         : ${dispApGear}  (${t('ap.bd.gear.note')})`,
    ...(showBase ? [] : [`${t('ap.bd.buffs')}        : ${dispApBuff}  (${t('ap.bd.buffs.note')})`]),
    `${'─'.repeat(30)}`,
    `${t('ap.bd.total')}        : ${ap}`,
  ].join('\n');

  container.innerHTML = `
    <div class="stats-mode-toggle">
      <button type="button" class="mode-btn ${viewMode === 'base'   ? 'active' : ''}" data-mode="base">${t('overview.mode.base')}</button>
      <button type="button" class="mode-btn ${viewMode === 'buffed' ? 'active' : ''}" data-mode="buffed">${t('overview.mode.buffed')}</button>
    </div>
    <div class="stat-row">
      <div class="stat-item" title="${escapeHtml(apTip)}">
        <span class="stat-label">${t('stat.ap')}</span>
        <span class="stat-value">${ap}${apDelta}</span>
      </div>
      <div class="stat-item" title="${escapeHtml(critTip)}">
        <span class="stat-label">${t('stat.crit')}</span>
        <span class="stat-value">${(critPct * 100).toFixed(2)}%${critDelta}</span>
        <span class="stat-substat muted">${cs.inBerserkerStance ? t('stat.crit.berserker') : t('stat.crit.battle')}</span>
      </div>
      ${item(t('stat.strength'),  stats.strength,  cmpStats?.strength)}
      ${item(t('stat.agility'),   stats.agility,   cmpStats?.agility)}
      ${item(t('stat.stamina'),   stats.stamina,   cmpStats?.stamina)}
      ${(() => {
        // Armor: 'base' = gear armor sum + 2 × base agi (matches in-town
        // character sheet). 'buffed' = same formula but with buffed agi
        // (× attrMult from Kings/Zandalar) plus any flat-armor buff effects.
        const armorVal = showBase ? cs.armor : buffed.armor;
        const cmpArmor = cmpCs == null
          ? undefined
          : showBase ? cmpCs.armor : cmpBuffed!.armor;
        return item(t('stat.armor'), armorVal, cmpArmor);
      })()}
      <!-- Intellect / Spirit dropped: warrior doesn't use them and they
           just push the AP / Crit / Strength columns out of view in
           narrow compare-mode columns. -->

    </div>
    <details class="stat-breakdown">
      <summary class="muted small">${t('overview.breakdown.summary')}</summary>
      <div class="breakdown-row">
        <div>
          <strong>${t('stat.ap')}:</strong>
          <span>${t('ap.bd.classBase')}: <b>${ab.classBase}</b></span>
          + <span>${t('ap.bd.strength')}: <b>${dispApStr}</b></span>
          + <span>${t('ap.bd.gear')}: <b>${dispApGear}</b></span>
          ${showBase ? '' : `+ <span>${t('ap.bd.buffs')}: <b>${dispApBuff}</b></span>`}
          = <b>${ap}</b>
        </div>
        <div>
          <strong>${t('stat.crit')}:</strong>
          <span>${t('crit.bd.classBase')}: <b>${cb.classBase.toFixed(2)}%</b></span>
          + <span>${t('crit.bd.agility')}: <b>${dispCritAgi.toFixed(2)}%</b></span>
          + <span>${t('crit.bd.cruelty')}: <b>${cb.fromCruelty.toFixed(2)}%</b></span>
          + <span>${t('crit.bd.stance')}: <b>${cb.fromStance.toFixed(2)}%</b></span>
          + <span>${t('crit.bd.gear')}: <b>${dispCritGear.toFixed(2)}%</b></span>
          ${showBase ? '' : `+ <span>${t('crit.bd.buffs')}: <b>${dispCritBuff.toFixed(2)}%</b></span>`}
          = <b>${(critPct * 100).toFixed(2)}%</b>
        </div>
      </div>
    </details>
    <p class="muted small note">${showBase ? t('overview.charSheetNote.base') : t('overview.charSheetNote.buffed')}</p>
  `;

  // Wire the toggle. The caller-supplied callback is what triggers a
  // re-render in main.ts; we just relay the picked mode.
  if (opts?.onToggleViewMode) {
    container.querySelectorAll<HTMLButtonElement>('.mode-btn').forEach((btn) => {
      btn.addEventListener('click', () => {
        const mode = btn.getAttribute('data-mode') as StatsViewMode;
        if (mode && mode !== viewMode) opts.onToggleViewMode!(mode);
      });
    });
  }
}
